import { useMemo } from 'react';
import {
  asBool,
  asString,
  cloneDoc,
  ensureObject,
  objectMap,
  type GraphAccessStatus,
  type GraphDomainsDoc,
  type GraphFeature,
} from '../../../lib/graph-domains-form-utils';
import {
  CheckboxField,
  FieldLabel,
  ItemCard,
  RemoveButton,
  SectionBox,
  SubsectionTitle,
  TextInput,
} from '../tenant-config-form/FormPrimitives';

interface GraphDomainsFormProps {
  doc: GraphDomainsDoc;
  onChange: (doc: GraphDomainsDoc) => void;
}

const FEATURES: GraphFeature[] = ['notifications', 'approvals'];

function updateDoc(onChange: (doc: GraphDomainsDoc) => void, doc: GraphDomainsDoc, mutator: (next: GraphDomainsDoc) => void) {
  const next = cloneDoc(doc);
  mutator(next);
  onChange(next);
}

function featureLabel(feature: GraphFeature): string {
  return feature === 'notifications' ? 'Notifications' : 'Approvals';
}

function FeatureBlock({
  feature,
  status,
  onChange,
}: {
  feature: GraphFeature;
  status: GraphAccessStatus;
  onChange: (next: GraphAccessStatus) => void;
}) {
  return (
    <div className="space-y-2">
      <SubsectionTitle>{featureLabel(feature)}</SubsectionTitle>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <CheckboxField
          label="Access granted"
          checked={asBool(status.accessGranted)}
          onChange={(v) => onChange({ ...status, accessGranted: v })}
        />
        <CheckboxField
          label="Pending request"
          checked={asBool(status.pendingRequest)}
          onChange={(v) => onChange({ ...status, pendingRequest: v })}
        />
        <FieldLabel label="Admin email">
          <TextInput
            value={asString(status.adminEmail)}
            onChange={(v) => onChange({ ...status, adminEmail: v || undefined })}
          />
        </FieldLabel>
        <FieldLabel label="Granted by">
          <TextInput
            value={asString(status.grantedBy)}
            onChange={(v) => onChange({ ...status, grantedBy: v || undefined })}
          />
        </FieldLabel>
        <FieldLabel label="Granted at">
          <TextInput
            value={asString(status.grantedAt)}
            onChange={(v) => onChange({ ...status, grantedAt: v || undefined })}
            mono
          />
        </FieldLabel>
        <FieldLabel label="Requested by">
          <TextInput
            value={asString(status.requestedBy)}
            onChange={(v) => onChange({ ...status, requestedBy: v || undefined })}
          />
        </FieldLabel>
        <FieldLabel label="Requested at" className="md:col-span-2">
          <TextInput
            value={asString(status.requestedAt)}
            onChange={(v) => onChange({ ...status, requestedAt: v || undefined })}
            mono
          />
        </FieldLabel>
      </div>
    </div>
  );
}

export function GraphDomainsForm({ doc, onChange }: GraphDomainsFormProps) {
  const domains = useMemo(() => objectMap(doc.domains), [doc.domains]);
  const domainNames = Object.keys(domains).sort();

  const addDomain = () => {
    const name = window.prompt('Domain name (e.g. contoso.com)');
    if (!name?.trim()) return;
    const key = name.trim().toLowerCase();
    if (domains[key]) {
      window.alert('That domain already exists.');
      return;
    }
    updateDoc(onChange, doc, (next) => {
      ensureObject(next, 'domains')[key] = {};
    });
  };

  const renameDomain = (oldKey: string) => {
    const name = window.prompt('Domain name', oldKey);
    if (!name?.trim() || name.trim().toLowerCase() === oldKey) return;
    const newKey = name.trim().toLowerCase();
    if (domains[newKey]) {
      window.alert('That domain already exists.');
      return;
    }
    updateDoc(onChange, doc, (next) => {
      const map = ensureObject(next, 'domains');
      map[newKey] = map[oldKey];
      delete map[oldKey];
    });
  };

  const removeDomain = (key: string) => {
    if (!window.confirm(`Remove domain "${key}"?`)) return;
    updateDoc(onChange, doc, (next) => {
      const map = ensureObject(next, 'domains');
      delete map[key];
    });
  };

  const updateFeature = (domainKey: string, feature: GraphFeature, status: GraphAccessStatus) => {
    updateDoc(onChange, doc, (next) => {
      const domain = ensureObject(ensureObject(next, 'domains'), domainKey) as Record<string, GraphAccessStatus>;
      domain[feature] = status;
    });
  };

  return (
    <div className="space-y-4 text-sm">
      <SectionBox title="Registry metadata">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <FieldLabel label="Version" hint="Updated automatically on save">
            <TextInput value={asString(doc.version)} onChange={() => {}} disabled mono />
          </FieldLabel>
          <FieldLabel label="Last updated" hint="Updated automatically on save">
            <TextInput value={asString(doc.lastUpdated)} onChange={() => {}} disabled mono />
          </FieldLabel>
        </div>
      </SectionBox>

      <SectionBox title="Domains" onAdd={addDomain} addLabel="Add domain">
        {domainNames.length === 0 ? (
          <p className="text-xs text-gray-500">No domains registered yet.</p>
        ) : (
          domainNames.map((domainKey) => {
            const entry = domains[domainKey];
            return (
              <ItemCard key={domainKey}>
                <div className="flex items-center justify-between gap-2">
                  <button
                    type="button"
                    onClick={() => renameDomain(domainKey)}
                    className="text-sm font-semibold text-gray-900 dark:text-gray-100 hover:text-blue-600 dark:hover:text-blue-400"
                    title="Click to rename"
                  >
                    {domainKey}
                  </button>
                  <RemoveButton onClick={() => removeDomain(domainKey)} label={`Remove ${domainKey}`} />
                </div>
                <div className="space-y-4 pt-2">
                  {FEATURES.map((feature) => (
                    <FeatureBlock
                      key={feature}
                      feature={feature}
                      status={(entry[feature] as GraphAccessStatus) || {}}
                      onChange={(next) => updateFeature(domainKey, feature, next)}
                    />
                  ))}
                </div>
              </ItemCard>
            );
          })
        )}
      </SectionBox>

      <p className="text-[11px] text-gray-500 dark:text-gray-400">
        Version and last updated are bumped by the server when you save. Use the YAML tab for bulk edits.
      </p>
    </div>
  );
}
